/**
 * TileRenderer.ts
 * Tile rendering helpers — maps tile IDs to tileset source rects.
 *
 * The tileset is a 256 × 96 px PNG (16 columns × 6 rows of 16 × 16 tiles).
 * Tile IDs are 1-based (firstgid = 1 in Tiled); tileId 0 means empty.
 *
 * This module is platform-agnostic: it only returns source rect data.
 * The actual draw call (drawImage / Skia <Image>) happens in GameCanvas files.
 *
 * Phase 3 — Art, Audio & Polish.
 * See ROADMAP.md Phase 3.
 *
 * @module TileRenderer
 */

import { TILE_SIZE } from '../../constants/gameConstants';

// DECISION: Tileset is native 16 px per tile — 1:1 with world TILE_SIZE.
// No upscale; drawImage maps source rect directly to destination.
/** Pixel size of one tile in the tileset PNG. */
export const TILESET_TILE_PX = 16;

/** Tileset sheet dimensions — World 1 Sunmeadow tileset is 256 × 96 px. */
const TILESET_SHEET_WIDTH = 256;
const TILESET_COLS = TILESET_SHEET_WIDTH / TILESET_TILE_PX; // 16

/**
 * Compute the source X/Y in the tileset PNG for a given 1-based tile ID.
 * Returns null for empty tiles (tileId === 0).
 */
export function tileSourceRect(tileId: number): { sx: number; sy: number } | null {
  if (tileId <= 0) return null;
  const zeroId = tileId - 1; // convert from 1-based to 0-based
  const col = zeroId % TILESET_COLS;
  const row = Math.floor(zeroId / TILESET_COLS);
  return {
    sx: col * TILESET_TILE_PX,
    sy: row * TILESET_TILE_PX,
  };
}

/**
 * Compute the tiles visible within the current camera viewport.
 * Returns col/row ranges clamped to the map bounds.
 *
 * @param camX     - Camera world X.
 * @param camY     - Camera world Y.
 * @param vpW      - Viewport width in pixels.
 * @param vpH      - Viewport height in pixels.
 * @param mapCols  - Map width in tiles.
 * @param mapRows  - Map height in tiles.
 */
export function visibleTileRange(
  camX: number,
  camY: number,
  vpW: number,
  vpH: number,
  mapCols: number,
  mapRows: number,
): { colStart: number; colEnd: number; rowStart: number; rowEnd: number } {
  const colStart = Math.max(0, Math.floor(camX / TILE_SIZE));
  const colEnd   = Math.min(mapCols - 1, Math.ceil((camX + vpW) / TILE_SIZE));
  const rowStart = Math.max(0, Math.floor(camY / TILE_SIZE));
  const rowEnd   = Math.min(mapRows - 1, Math.ceil((camY + vpH) / TILE_SIZE));
  return { colStart, colEnd, rowStart, rowEnd };
}
